<script setup lang="ts">
import { boundsAlgoliaIsValid } from '@/helpers/search/geo'

import 'mapbox-gl/dist/mapbox-gl.css'

import type {
  Anchor,
  LngLat,
  LngLatBounds,
  Map as MapInstance,
  MapMouseEvent,
  FilterSpecification,
} from 'mapbox-gl'
import type {
  SearchHouse,
  SearchHouseCoordinatesFormatted,
} from 'lc-services/types'
import type { Bounds, QueryModelType } from '~/types/search/types'

const {
  coordinatesBoundaries = [],
  coordinatesHitsMap = [],
  geographicAreaActive = false,
  hideZoom = false,
  hitsListIds = [],
  hoveredHouseId,
  isLoading = false,
  searchHousesPayload,
  template,
} = defineProps<{
  coordinatesBoundaries: [number, number][]
  coordinatesHitsMap: SearchHouseCoordinatesFormatted[]
  geographicAreaActive?: boolean
  hideZoom?: boolean
  hitsListIds: number[]
  hoveredHouseId?: number
  isLoading?: boolean
  searchHousesPayload: QueryModelType
  template?: 'map' | 'split' | 'list'
}>()
const emits = defineEmits<{
  'track-house': [houseId: number]
  'map-moved': [bounds: Bounds]
}>()

const config = useRuntimeConfig()
const { isMobile } = useBreakpoint()
const { trackEvent } = useTracking()
const { getHouseInformations, housesStorage } = useSearchSplitMap()

const DEFAULT_LAYERS = [
  { key: 'layer-simple', type: 'simple', visited: false },
  { key: 'layer-simple-visited', type: 'simple', visited: true },
  { key: 'layer-icon', type: 'house', visited: false },
  { key: 'layer-icon-visited', type: 'house', visited: true },
] as const
const HOVER_LAYERS = [
  { key: 'layer-icon-hover', type: 'house' },
  { key: 'layer-simple-hover', type: 'simple' },
] as const

const ICON_SOURCES = [
  {
    src: '/mapbox/house-default.png',
    id: 'house-default',
  },
  {
    src: '/mapbox/house-hover.png',
    id: 'house-hover',
  },
  {
    src: '/mapbox/house-clicked.png',
    id: 'house-clicked',
  },
  {
    src: '/mapbox/house-visited.png',
    id: 'house-visited',
  },
  {
    src: '/mapbox/simple-default.png',
    id: 'simple-default',
  },
  {
    src: '/mapbox/simple-hover.png',
    id: 'simple-hover',
  },
  {
    src: '/mapbox/simple-visited.png',
    id: 'simple-visited',
  },
]

const MOVE_THRESHOLD_KM = 5
const POPUP_HEIGHT = 317
const POPUP_OFFSET = 20
const POPUP_WIDTH = 320

const map = ref<MapInstance>()
const currentMove = ref(false)
const isOpen = ref(false)
const position = ref<[number, number]>([0, 0])
const content = ref<SearchHouse>()
const featureId = ref<number>()
const featureClickedId = ref<number>()
const lastBounds = ref<LngLatBounds | null>(null)
const anchorPopin = ref<Anchor>('top')
const houseVisited = ref<number[]>([])

const data = computed(() => {
  const zoom = map.value?.getZoom()

  return {
    type: 'FeatureCollection',
    features: currentMove.value
      ? []
      : coordinatesHitsMap.map((hit) => ({
          ...hit,
          properties: {
            styleMapIcon:
              Number(zoom) > 11 || hitsListIds.includes(hit.id)
                ? 'house'
                : 'simple',
          },
        })),
  }
})

const anchorOffsetPopin = computed(() => {
  if (anchorPopin.value === 'bottom') return [0, -POPUP_OFFSET]
  else if (anchorPopin.value === 'top') return [0, POPUP_OFFSET]
  else if (anchorPopin.value === 'left') return [POPUP_OFFSET, 0]
  else if (anchorPopin.value === 'right') return [-POPUP_OFFSET, 0]
  else if (anchorPopin.value === 'top-right')
    return [POPUP_OFFSET, POPUP_OFFSET]
  else if (anchorPopin.value === 'bottom-right')
    return [-POPUP_OFFSET, POPUP_OFFSET]
  else if (anchorPopin.value === 'bottom-left')
    return [POPUP_OFFSET, POPUP_OFFSET]
  else if (anchorPopin.value === 'top-left')
    return [-POPUP_OFFSET, POPUP_OFFSET]
  return [0, 0]
})

const filters = computed(() => {
  return DEFAULT_LAYERS.reduce(
    (acc, layer) => {
      acc[layer.key] = createFilter(layer.type, layer.visited)
      return acc
    },
    {} as Record<string, FilterSpecification>,
  )
})

const onCreated = (mapInstance: MapInstance) => {
  map.value = mapInstance
  map.value.touchZoomRotate.disableRotation()
  lastBounds.value = map.value.getBounds()
}

const onLoad = () => {
  const layers = map.value?.getStyle()?.layers
  layers?.forEach((layer) => {
    if (
      layer.id.includes('road') ||
      layer.id.includes('highway') ||
      layer.id.includes('street') ||
      layer.id.includes('hillshade')
    ) {
      map.value?.setLayoutProperty(layer.id, 'visibility', 'none')
    }
  })

  map.value?.setFilter('poi-label', ['!=', 'class', 'lodging'])
}

const createFilter = (type: string, visited: boolean): FilterSpecification => {
  return [
    'all',
    visited
      ? ['in', ['id'], ['literal', houseVisited.value]]
      : ['!', ['in', ['id'], ['literal', houseVisited.value]]],
    ['==', ['get', 'styleMapIcon'], type],
  ]
}

const setFeatureStateHover = (id: number, hover: boolean) => {
  map.value?.setFeatureState({ source: 'points', id: id }, { hover })
}

const setFeatureStateClicked = (id: number, clicked: boolean) => {
  map.value?.setFeatureState({ source: 'points', id: id }, { clicked })
}

const resetHoverState = () => {
  if (map.value?.getLayer('layer-icon-hover')) {
    const features = map.value?.queryRenderedFeatures({
      layers: ['layer-icon-hover'],
    })
    features?.forEach((f) => {
      if (f.id && f.state?.hover) setFeatureStateHover(Number(f.id), false)
    })
  }
  if (map.value?.getLayer('layer-simple-hover')) {
    const features = map.value?.queryRenderedFeatures({
      layers: ['layer-simple-hover'],
    })
    features?.forEach((f) => {
      if (f.id && f.state?.hover) setFeatureStateHover(Number(f.id), false)
    })
  }
}

const resetClickedState = async () => {
  if (map.value?.getLayer('layer-icon-clicked')) {
    const features = map.value?.queryRenderedFeatures({
      layers: ['layer-icon-clicked'],
    })
    features.forEach((f) => {
      if (f.id && f.state?.clicked) setFeatureStateClicked(Number(f.id), false)
    })
  }
  await nextTick()
}

const onMousemove = (event: MapMouseEvent) => {
  const feature = event.features?.[0]
  if (!feature) return
  if (map.value) map.value.getCanvas().style.cursor = 'pointer'

  if (featureId.value) setFeatureStateHover(featureId.value, false)
  featureId.value = Number(feature.id)
  setFeatureStateHover(featureId.value, true)
}

const onMouseleave = () => {
  if (!map.value || !featureId.value) return

  map.value.getCanvas().style.cursor = ''
  setFeatureStateHover(featureId.value, false)
  featureId.value = undefined
}

const onClick = async (event: MapMouseEvent) => {
  const feature = event.features?.find((f) => f.state?.hover)
  if (!feature?.id) return

  if (!houseVisited.value.includes(Number(feature?.id))) {
    houseVisited.value = [...houseVisited.value, Number(feature?.id)]

    DEFAULT_LAYERS.forEach(({ key }) => {
      map.value?.setFilter(key, filters.value[key])
    })
  }

  setAnchorPopin(event.lngLat)
  await resetClickedState()
  featureClickedId.value = Number(feature.id)

  setFeatureStateClicked(Number(feature.id), true)

  await openPopup(Number(feature.id), feature.geometry.coordinates)
}

const closePopup = async () => {
  isOpen.value = false
  content.value = undefined
  await resetClickedState()
}

const setAnchorPopin = (markerPosition: LngLat) => {
  if (!map.value) return
  const projected = map.value.project(markerPosition)
  const { width, height } = map.value.getCanvas().getBoundingClientRect()

  let anchor: Anchor = 'bottom'

  const spaceRight = width - projected.x - POPUP_OFFSET
  const spaceLeft = projected.x - POPUP_OFFSET
  const spaceBottom = height - projected.y - POPUP_OFFSET
  const spaceTop = projected.y - POPUP_OFFSET

  if (spaceTop > POPUP_HEIGHT) {
    anchor = 'bottom'
    if (spaceLeft > POPUP_WIDTH && spaceBottom > POPUP_HEIGHT / 2) {
      anchor = 'right'
    } else if (spaceRight > POPUP_WIDTH && spaceBottom > POPUP_HEIGHT / 2) {
      anchor = 'left'
    } else if (spaceRight < POPUP_WIDTH / 2) {
      anchor = 'bottom-right'
    } else if (spaceLeft < POPUP_WIDTH / 2) {
      anchor = 'bottom-left'
    }
  } else if (spaceBottom > POPUP_HEIGHT) {
    anchor = 'top'
    if (spaceLeft > POPUP_WIDTH && spaceTop > POPUP_HEIGHT / 2) {
      anchor = 'right'
    } else if (spaceRight > POPUP_WIDTH && spaceTop > POPUP_HEIGHT / 2) {
      anchor = 'left'
    } else if (spaceRight < POPUP_WIDTH / 2) {
      anchor = 'top-right'
    } else if (spaceLeft < POPUP_WIDTH / 2) {
      anchor = 'top-left'
    }
  }

  anchorPopin.value = anchor
}

const openPopup = async (houseId: number, coords: [number, number]) => {
  position.value = [coords[0], coords[1]]
  isOpen.value = true

  let house = housesStorage.value.find((h) => h.id === houseId)
  if (!house) {
    await getHouseInformations({
      houseId: houseId.toString(),
      searchHousesPayload,
    })
    house = housesStorage.value.find((h) => h.id === houseId)
  }

  content.value = house

  // Wait for mapbox to send the correct informations to the popup
  await nextTick()

  const zoom = map.value?.getZoom()

  trackEvent({
    event: 'map_house_icon_click',
    type:
      Number(zoom) > 11 || hitsListIds.includes(houseId) ? 'house' : 'circle',
    zoom: zoom ? Math.round(zoom) : undefined,
  })
}

const getDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
) => {
  const R = 6371
  const rad = Math.PI / 180
  const dLat = (lat2 - lat1) * rad
  const dLon = (lon2 - lon1) * rad

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * rad) *
      Math.cos(lat2 * rad) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

const checkBoundsChange = useDebounceFn(() => {
  if (!map.value) return

  const newBounds = map.value.getBounds()
  if (!newBounds || !lastBounds.value) return

  const lastCenter = lastBounds.value.getCenter()
  const newCenter = newBounds.getCenter()

  const distanceMoved = getDistance(
    lastCenter.lat,
    lastCenter.lng,
    newCenter.lat,
    newCenter.lng,
  )

  if (
    map.value.getZoom() >= 8 ||
    (map.value.getZoom() < 8 && distanceMoved > MOVE_THRESHOLD_KM)
  ) {
    lastBounds.value = newBounds

    const insideBoundingBox: Bounds = [
      newBounds.getNorthEast().lat,
      newBounds.getNorthEast().lng,
      newBounds.getSouthWest().lat,
      newBounds.getSouthWest().lng,
    ]

    if (boundsAlgoliaIsValid(insideBoundingBox)) {
      emits('map-moved', insideBoundingBox)
    }
  }
}, 800)

watch(
  () => template,
  async () => {
    if (template === 'split' && coordinatesHitsMap.length) {
      const min = coordinatesBoundaries?.[0]
      const max = coordinatesBoundaries?.[1]
      if (!min.length || !max.length) return

      await nextTick()
      map.value?.resize()
      map.value?.fitBounds([min, max], { duration: 500 })
    }
    await closePopup()
  },
)

watch(
  () => coordinatesBoundaries,
  async () => {
    if (geographicAreaActive) return
    const min = coordinatesBoundaries?.[0]
    const max = coordinatesBoundaries?.[1]
    if (!min.length || !max.length) return

    currentMove.value = true
    map.value?.fitBounds([min, max], { duration: 500 })
  },
)

watch(
  () => hoveredHouseId,
  (value) => {
    if (value) {
      const layer = 'layer-icon-hover'

      if (map.value?.getLayer(layer)) {
        const features = map.value?.queryRenderedFeatures({
          layers: [layer],
        })

        const visiblePoint =
          features?.some((feature) => feature.id === value) || false
        if (visiblePoint) setFeatureStateHover(value, true)
      }
    } else {
      resetHoverState()
    }
  },
)

const mapbox = ref<typeof import('@studiometa/vue-mapbox-gl')>()

// 'onMounted' is only invoked client-side and should not block the initial rendering
// + as mapbox-gl uses WebGL, there is no need to include it in server context
// + as mapbox-gl is a huge script, better to load it asynchronously
onMounted(async () => {
  mapbox.value = await import('@studiometa/vue-mapbox-gl')
})
</script>

<template>
  <div class="relative">
    <div
      v-if="isLoading"
      :class="[
        'absolute z-10 flex w-full items-center justify-center',
        isMobile && template === 'map' ? 'top-32' : 'top-3',
      ]"
    >
      <div class="flex items-center rounded bg-white px-4 py-2">
        <div
          class="inline-block size-6 animate-spin rounded-full border-[3px] border-gray-700 border-t-transparent"
          role="status"
          aria-label="loading"
        />
      </div>
    </div>
    <mapbox.MapboxMap
      v-if="mapbox"
      :class="['h-full', { 'hidden-zoom': hideZoom }]"
      map-style="mapbox://styles/mapbox/streets-v12"
      :access-token="config.public.mapboxApiKey"
      :bounds="coordinatesBoundaries"
      :drag-rotate="false"
      :fit-bounds-options="{
        padding: isMobile ? 10 : 20,
        maxZoom: 15,
      }"
      :pitch-with-rotate="false"
      @mb-created="onCreated"
      @mb-load="onLoad"
      @mb-dragend="checkBoundsChange"
      @mb-moveend="currentMove = false"
      @mb-zoomend="
        (event: MapMouseEvent) => {
          if (event.originalEvent) checkBoundsChange()
        }
      "
    >
      <mapbox.MapboxImages :sources="ICON_SOURCES">
        <mapbox.MapboxSource
          id="points"
          :options="{
            type: 'geojson',
            cluster: false,
            data: data,
          }"
        />

        <mapbox.MapboxLayer
          v-for="layer in DEFAULT_LAYERS"
          :id="layer.key"
          :key="layer.key"
          :options="{
            type: 'symbol',
            source: 'points',
            filter: filters[layer.key],
            layout: {
              'icon-image': layer.visited
                ? `${layer.type}-visited`
                : `${layer.type}-default`,
              'icon-size': 0.3,
              'icon-allow-overlap': true,
            },
          }"
          @mb-mousemove="onMousemove"
          @mb-mouseleave="onMouseleave"
        />

        <mapbox.MapboxLayer
          v-for="layer in HOVER_LAYERS"
          :id="layer.key"
          :key="layer.key"
          :options="{
            type: 'symbol',
            source: 'points',
            filter: ['==', ['get', 'styleMapIcon'], layer.type],
            layout: {
              'icon-image': `${layer.type}-hover`,
              'icon-size': 0.3,
              'icon-allow-overlap': true,
            },
            paint: {
              'icon-opacity': [
                'case',
                ['boolean', ['feature-state', 'hover'], false],
                1,
                0,
              ],
            },
          }"
          @mb-click="onClick"
          @mb-mouseleave="onMouseleave"
        />

        <mapbox.MapboxLayer
          id="layer-icon-clicked"
          :options="{
            type: 'symbol',
            source: 'points',
            layout: {
              'icon-image': 'house-clicked',
              'icon-size': 0.3,
              'icon-allow-overlap': true,
            },
            paint: {
              'icon-opacity': [
                'case',
                ['boolean', ['feature-state', 'clicked'], false],
                1,
                0,
              ],
            },
          }"
        />
      </mapbox.MapboxImages>

      <mapbox.MapboxPopup
        v-if="isOpen"
        :key="position.join('-')"
        :anchor="anchorPopin"
        :close-button="false"
        :lng-lat="position"
        :offset="anchorOffsetPopin"
        class-name="search-popup-mapbox"
        max-width="260"
        @mb-close="closePopup"
      >
        <LazySearchMapPopupContentSkeleton v-if="!content" />
        <LazySearchMapPopupContentV2Mobile
          v-else-if="isMobile && content"
          :house="content"
          @close="isOpen = false"
          @track-house="$emit('track-house', $event)"
        />
        <LazySearchMapPopupContentV2
          v-else-if="!isMobile && content"
          :house="content"
          @close="isOpen = false"
          @track-house="$emit('track-house', $event)"
        />
      </mapbox.MapboxPopup>
      <mapbox.MapboxNavigationControl
        position="bottom-right"
        :show-compass="false"
      />
    </mapbox.MapboxMap>
  </div>
</template>

<style scoped>
:deep(.search-popup-mapbox .mapboxgl-popup-content) {
  @apply p-0 rounded;
}

:deep(.search-popup-mapbox .mapboxgl-popup-tip) {
  @apply hidden;
}

@media screen and (max-width: 767px) {
  :deep(.search-popup-mapbox.mapboxgl-popup) {
    @apply block !transform-none bottom-4 left-4 right-4 top-auto z-10;
  }
}

:deep(.hidden-zoom .mapboxgl-ctrl-group) {
  @apply hidden;
}
</style>
